import type { ContentDeviceDiagnostics, ContentRuntimeState, DeviceSession } from "@teamtable/api";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import {
  drainQueue,
  restoreEvents,
  serializedBytes,
  setEventContext,
  trackEvent,
} from "../analytics/eventQueue.js";
import { apiClient, PayloadTooLargeError } from "../api/client.js";
import { mergeDeviceSession, prepareRuntimeState } from "./runtimeState.js";

const HEARTBEAT_INTERVAL_MS = 15 * 1000; // 15 seconds
const MAX_HEARTBEAT_BYTES = 96 * 1024; // Below Express's 100 KiB JSON limit, including telemetry.

// A table has no watchdog to reload itself, so a boot that cannot reach
// platform-api must not be mistaken for an unpaired device — giving up sends
// the table to the device-setup screen with nothing to bring it back. Retry an
// unreachable API on this backoff (~23s in total) before conceding. A 401 is a
// definite answer and never retried, so enrolling a device stays immediate.
const SESSION_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 8000];

/** Whether the API answered, refused the token, or could not be reached. */
type SessionAttempt = "ok" | "rejected" | "unreachable";

interface DeviceAuthContextData {
  session: DeviceSession | null;
  loading: boolean;
  diagnostics?: ContentDeviceDiagnostics;
  onAuthenticated: (token: string) => void;
  reportBundleState: (state: ContentRuntimeState) => void;
}

const DeviceAuthContext = createContext<DeviceAuthContextData>({
  session: null,
  loading: true,
  onAuthenticated: () => {},
  reportBundleState: () => {},
});

export function useDeviceAuth() {
  return useContext(DeviceAuthContext);
}

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function setDeviceCookie(token: string) {
  const maxAge = 10 * 365 * 24 * 60 * 60;
  const domain = window.serverConfig?.COOKIE_DOMAIN;
  const isSecure = window.location.protocol === "https:";
  let cookie = `device-auth-token=${token}; path=/; max-age=${maxAge}`;
  if (domain) cookie += `; domain=${domain}`;
  if (isSecure) cookie += "; SameSite=None; Secure";
  else cookie += "; SameSite=Lax";
  document.cookie = cookie;
}

function clearDeviceCookie() {
  const domain = window.serverConfig?.COOKIE_DOMAIN;
  let cookie = "device-auth-token=; path=/; max-age=0";
  if (domain) cookie += `; domain=${domain}`;
  document.cookie = cookie;
}

export function DeviceAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<DeviceSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [diagnostics, setDiagnostics] = useState<ContentDeviceDiagnostics>();
  const sessionRef = useRef<DeviceSession | null>(null);
  const runtimeRef = useRef<ContentRuntimeState>({ bundle_initialized: false });
  const reportBundleState = useCallback((value: ContentRuntimeState) => {
    runtimeRef.current = { ...runtimeRef.current, ...value };
  }, []);
  const acceptSession = useCallback((value: DeviceSession | null) => {
    sessionRef.current = value;
    setEventContext(value);
    setSession(value);
  }, []);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped by every fresh validation so a run already in flight, or a retry
  // already scheduled, cannot land on top of a newer one.
  const attemptRef = useRef(0);

  const attemptSession = useCallback(
    async (generation: number): Promise<SessionAttempt> => {
      const token = getCookie("device-auth-token");
      if (!token) {
        acceptSession(null);
        setDiagnostics(undefined);
        return "rejected";
      }

      try {
        const res = await apiClient["/api/v1/content/session"]["get"]({
          parameters: {},
          body: null,
        });
        if (generation !== attemptRef.current) return "unreachable";
        if (res.code === 200) {
          acceptSession(res.body);
          setDiagnostics(res.body.diagnostics);
          return "ok";
        }
        if (res.code === 401) {
          // Token rejected outright — the device really is unpaired, so clear
          // the pairing cookie and let the app send it to device setup.
          clearDeviceCookie();
          acceptSession(null);
          setDiagnostics(undefined);
          return "rejected";
        }
        // A server error says nothing about whether this device is paired, so
        // keep the cookie and treat it as worth retrying, the same way the catch
        // block below treats a network failure.
        acceptSession(null);
        return "unreachable";
      } catch {
        if (generation !== attemptRef.current) return "unreachable";
        acceptSession(null);
        return "unreachable";
      }
    },
    [acceptSession],
  );

  const validateSession = useCallback(async () => {
    setLoading(true);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    const generation = ++attemptRef.current;

    // `loading` stays true for the whole sequence, so a table that recovers
    // part-way through simply carries on with no reload.
    let retries = 0;
    const run = async () => {
      const outcome = await attemptSession(generation);
      if (generation !== attemptRef.current) {
        return;
      }
      if (outcome === "unreachable" && retries < SESSION_RETRY_DELAYS_MS.length) {
        const delay = SESSION_RETRY_DELAYS_MS[retries];
        retries += 1;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          void run();
        }, delay);
        return;
      }
      setLoading(false);
    };

    await run();
  }, [attemptSession]);

  useEffect(() => {
    // Check for token in URL query param (redirect from user-portal after pairing)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      setDeviceCookie(urlToken);
      params.delete("token");
      const remaining = params.toString();
      window.history.replaceState({}, "", remaining ? `/?${remaining}` : "/");
    }
    validateSession();

    return () => {
      attemptRef.current += 1;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [validateSession]);

  const deviceId = session?.device_id;
  useEffect(() => {
    if (!deviceId) return;
    trackEvent("session_started");
    const generation = attemptRef.current;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let maxHeartbeatBytes = MAX_HEARTBEAT_BYTES;
    const sendHeartbeat = async () => {
      if (cancelled || generation !== attemptRef.current) return;
      const runtime_state = prepareRuntimeState({
        ...runtimeRef.current,
        context_revision: sessionRef.current?.context_revision,
      });
      const envelopeBytes = serializedBytes({ events: [], runtime_state });
      const events = drainQueue(maxHeartbeatBytes - envelopeBytes + 2);
      const body = { events, runtime_state };
      try {
        const res = await apiClient["/api/v1/content/devices/{id}/heartbeat"]["post"]({
          parameters: { id: deviceId },
          body,
        });
        if (cancelled || generation !== attemptRef.current) return;
        if (res.code === 401) {
          clearDeviceCookie();
          acceptSession(null);
          setDiagnostics(undefined);
          return;
        }
        if (res.code === 200) {
          if (res.body.session && sessionRef.current) {
            acceptSession(mergeDeviceSession(sessionRef.current, res.body.session));
          }
          const nextDiagnostics = res.body.session?.diagnostics ?? res.body.diagnostics;
          if (nextDiagnostics !== undefined) setDiagnostics(nextDiagnostics);
        } else {
          restoreEvents(events);
        }
      } catch (error) {
        if (!cancelled && generation === attemptRef.current) {
          if (error instanceof PayloadTooLargeError) {
            // Reduce from the actual rejected payload, even if it held only one event.
            maxHeartbeatBytes = Math.max(envelopeBytes, Math.floor(serializedBytes(body) / 2));
          }
          restoreEvents(events);
        }
      }
      if (!cancelled && generation === attemptRef.current) {
        timer = setTimeout(() => {
          void sendHeartbeat();
        }, HEARTBEAT_INTERVAL_MS);
      }
    };
    void sendHeartbeat();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [deviceId, acceptSession]);

  const onAuthenticated = useCallback(
    (token: string) => {
      acceptSession(null);
      runtimeRef.current = { bundle_initialized: false };
      setDiagnostics(undefined);
      setDeviceCookie(token);
      validateSession();
    },
    [validateSession, acceptSession],
  );

  return (
    <DeviceAuthContext.Provider
      value={{ session, loading, diagnostics, onAuthenticated, reportBundleState }}
    >
      {children}
    </DeviceAuthContext.Provider>
  );
}
